import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import LineExpandArrowsAltIcon from 'LuxKit/Icons/line/LineExpandArrowsAltIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Group from 'components/utils/Group'

interface Props {
  pinNames: Array<string>
  onEnlarge?: () => void
}

const Placeholder = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  width: 100%;
  height: 100%;
  padding: ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const EnlargeButton = styled(IconButton)`
  position: absolute;
  inset-block-start: ${rem(8)};
  inset-inline-end: ${rem(8)};
  z-index: 1;
`

/**
 * Static stand-in while a chat map card is out of view or over the live-slot cap.
 * Keeps layout height stable so the transcript does not jump on mount/unmount.
 */
function ChatMapCardPlaceholder({ pinNames, onEnlarge }: Props) {
  const intl = useIntl()
  const pinCount = pinNames.length
  const previewNames = pinNames.slice(0, 3)
  const remainingCount = Math.max(0, pinCount - previewNames.length)

  const title = intl.formatMessage({
    id: 'APiY2bsQaBC950dwG1jn',
    defaultMessage: 'Map',
    description: 'Title shown on the static placeholder for an in-chat map card that is not currently live',
  })

  const pinSummary = remainingCount > 0
    ? intl.formatMessage({
        id: 'eV_Zqi7lcloSxHfc8E3Q',
        defaultMessage: '{names} {remainingCount, plural, one {+# more place} other {+# more places}}',
        description: 'Truncated list of place names with overflow count on the static in-chat map card placeholder',
      }, {
        names: intl.formatList(previewNames, { type: 'unit' }),
        remainingCount,
      })
    : pinCount > 0
      ? intl.formatList(previewNames, { type: 'conjunction' })
      : intl.formatMessage({
          id: 'H0jamrS57h9IzPH-oZWe',
          defaultMessage: 'No places on this map yet',
          description: 'Empty-state copy for an in-chat map card placeholder when there are no pins',
        })

  const enlargeLabel = intl.formatMessage({
    id: 'iDbeTBxHryKNbcuitKHA',
    defaultMessage: 'Open map',
    description: 'Accessibility label for opening the main map from a static in-chat map placeholder',
  })

  return (
    <Placeholder>
      {onEnlarge && (
        <EnlargeButton
          kind="secondary"
          size="small"
          aria-label={enlargeLabel}
          onClick={onEnlarge}
        >
          <LineExpandArrowsAltIcon />
        </EnlargeButton>
      )}
      <Group direction="horizontal" gap={8} verticalAlign="center">
        <LineMapMarkerIcon size={20} />
        <BodyText variant="medium" weight="bold">{title}</BodyText>
      </Group>
      <BodyText variant="small" align="center" colour="neutral-two" lineClamp={2}>
        {pinSummary}
      </BodyText>
    </Placeholder>
  )
}

export default ChatMapCardPlaceholder
